/**
 * Generic customization types can't have selectors as such, but this file contains
 * helper functions that can be called from slice-specific selectors.
 * 
 */

import { assertAreDefined } from "../../util/assert";
import { DEFAULT_CONTOUR_FILENAME } from "../contouring/contouring-types";
import { StoreState } from "../store";
import { CustomizationBase, CustomizationObjectType, CustomizationOutput, CustomizationTreeViewDataItem, DicomRule, getModelTagsInOrder, METADATA_FILENAME_ATTRIBUTE, Model, ModelVisibility, OutputMetadataItem, TAG_LATEST, ValidationStatus, worstValidationStatus } from "./customization-types";
import { BackendValidationErrorViewModel, collectDicomAttributeIds, FormValidationError, FormValidationErrorSeverity } from "./store-errors";
import { sortByBodyPartDisplayOrder } from "./customization-helpers";

// A couple of complex types just to help visual studio code/tsc handle more complex selectors. These are not needed outside of selector files.
// export type Selector<S> = (state: StoreState) => S;
export type SelectorWithInputArg<S, T> = (state: StoreState, input: T) => S;

export const generateCustomizationTreeViewItems = (
    models: Model[],
    customizationBases: CustomizationBase[],
    outputEntities: Record<string, CustomizationOutput>,
    metadataEntities: Record<string, OutputMetadataItem>,
    validationEntities: Record<string, BackendValidationErrorViewModel>,
    formValidationErrors: FormValidationError[],
    areOutputChildrenValid: (output: CustomizationOutput, validationEntities: Record<string, BackendValidationErrorViewModel>, formErrors: FormValidationError[]) => ValidationStatus,
    dicomRuleEntities?: Record<string, DicomRule>,
) => {
    const bodyPartNodes: CustomizationTreeViewDataItem[] = [];

    /** Returns the worst form validation status (Error > Warning > Valid) for the given item IDs. */
    const getFormErrorStatus = (itemIds: string[]): ValidationStatus => {
        let status = ValidationStatus.Valid;
        for (const err of formValidationErrors) {
            if (itemIds.includes(err.itemId)) {
                if (err.severity === FormValidationErrorSeverity.Error) {
                    return ValidationStatus.Error;
                }
                if (err.severity === FormValidationErrorSeverity.Warning) {
                    status = worstValidationStatus(status, ValidationStatus.Warning);
                }
            }
        }
        return status;
    };

    for (const model of models) {

        // create body part parent node if it doesn't exist yet
        let bodyPartNode = bodyPartNodes.find(n => n.label === model.bodyPart);
        if (bodyPartNode === undefined) {
            bodyPartNode = {
                label: model.bodyPart,
                children: [],
                id: model.bodyPart,
                validationStatus: ValidationStatus.Valid,
                parentId: undefined,
                type: CustomizationObjectType.None,
                isModified: false,
                isAvailable: true,
                isDeprecated: false,
                isLatest: model.isLatest,
                version: model.version,
                modelVisibility: ModelVisibility.AlwaysShown,
            };
            bodyPartNodes.push(bodyPartNode);
        }

        const modelHasBackendError = validationEntities[model.id] && validationEntities[model.id]!.type === CustomizationObjectType.Model;
        const modelNode: CustomizationTreeViewDataItem = {
            label: model.label,
            id: model.id,
            children: [],
            type: CustomizationObjectType.Model,
            isModified: model.isModified,
            parentId: bodyPartNode.id,
            validationStatus: modelHasBackendError ? ValidationStatus.Error : ValidationStatus.Valid,
            isAvailable: model.isAvailable,
            isDeprecated: model.isDeprecated,
            modelVisibility: model.visibility,
            tags: model.tags,
            version: model.version,
            isLatest: model.isLatest,
        };

        bodyPartNode.children.push(modelNode);

        for (const customizationBase of customizationBases.filter(b => model.customizations.includes(b.id))) {
            // Backend errors on base, AE title rules, DICOM rules, DICOM attribute rules.
            // NOTE: `dicomAttributeHasBackendError` is pre-emptive — `convertBackendValidationErrorToViewModels`
            // currently rewrites DICOM-attribute backend errors to their parent DICOM rule (see TODO there),
            // so the store never holds a `DicomAttributeRule`-typed backend error today. Wired up here so
            // the rollup is correct the moment the converter is fixed; no behavior change in the meantime.
            const dicomAttributeIds = dicomRuleEntities ? collectDicomAttributeIds(customizationBase.dicomRules, dicomRuleEntities) : [];
            const baseHasBackendError = validationEntities[customizationBase.id] && validationEntities[customizationBase.id]!.type === CustomizationObjectType.CustomizationBase;
            const aeHasBackendError = customizationBase.aeTitleRules.some(aeId => validationEntities[aeId]?.type === CustomizationObjectType.AeTitleRule);
            const dicomHasBackendError = customizationBase.dicomRules.some(dId => validationEntities[dId]?.type === CustomizationObjectType.DicomRule);
            const dicomAttributeHasBackendError = dicomAttributeIds.some(daId => validationEntities[daId]?.type === CustomizationObjectType.DicomAttributeRule);

            let baseStatus: ValidationStatus = (baseHasBackendError || aeHasBackendError || dicomHasBackendError || dicomAttributeHasBackendError) ? ValidationStatus.Error : ValidationStatus.Valid;

            // Form validation errors on AE title rules, DICOM rules, and DICOM attribute rules
            const formErrorStatus = getFormErrorStatus([...customizationBase.aeTitleRules, ...customizationBase.dicomRules, ...dicomAttributeIds]);
            baseStatus = worstValidationStatus(baseStatus, formErrorStatus);

            const baseNode: CustomizationTreeViewDataItem = {
                label: customizationBase.customizationName,
                id: customizationBase.id,
                children: [],
                type: CustomizationObjectType.CustomizationBase,
                isModified: customizationBase.isModified,
                parentId: modelNode.id,
                validationStatus: baseStatus,
                isAvailable: model.isAvailable,
                isDeprecated: model.isDeprecated,
                modelVisibility: model.visibility,
                version: model.version,
                isLatest: model.isLatest,
            };
            modelNode.children.push(baseNode);

            if (customizationBase.outputs.length > 0) {
                baseNode.children = [];
                const outputs = customizationBase.outputs.map(oId => outputEntities[oId]);
                assertAreDefined(outputs);
                for (const output of outputs) {
                    const metadata = output.metadata.map(mId => metadataEntities[mId]);
                    const filename = metadata.find(m => m?.attribute === METADATA_FILENAME_ATTRIBUTE)?.value || DEFAULT_CONTOUR_FILENAME;

                    const outputHasBackendError = validationEntities[output.id] && validationEntities[output.id]!.type === CustomizationObjectType.CustomizationOutput;
                    const metadataHasBackendError = output.metadata.some(mId => validationEntities[mId] && validationEntities[mId]!.type === CustomizationObjectType.Metadata);
                    let outputStatus: ValidationStatus = (outputHasBackendError || metadataHasBackendError) ? ValidationStatus.Error : ValidationStatus.Valid;

                    // Check form validation errors on the output itself
                    outputStatus = worstValidationStatus(outputStatus, getFormErrorStatus([output.id]));

                    // Check output children (ROIs, targets, etc) via the callback
                    const childrenStatus = areOutputChildrenValid(output, validationEntities, formValidationErrors);
                    outputStatus = worstValidationStatus(outputStatus, childrenStatus);

                    const outputNode: CustomizationTreeViewDataItem = {
                        label: filename,
                        id: output.id,
                        children: [],
                        type: CustomizationObjectType.CustomizationOutput,
                        isModified: output.isModified,
                        parentId: baseNode.id,
                        validationStatus: outputStatus,
                        isAvailable: model.isAvailable,
                        isDeprecated: model.isDeprecated,
                        modelVisibility: model.visibility,
                        isLatest: model.isLatest,
                        version: model.version,
                    };
                    baseNode.children.push(outputNode);
                    baseNode.validationStatus = worstValidationStatus(baseNode.validationStatus, outputNode.validationStatus);
                }
            }

            modelNode.validationStatus = worstValidationStatus(modelNode.validationStatus, baseNode.validationStatus);
        }
    }

    // set modified & valid settings for top-level body-part nodes
    for (const bodyPartNode of bodyPartNodes) {
        bodyPartNode.validationStatus = bodyPartNode.children.reduce<ValidationStatus>(
            (acc, m) => worstValidationStatus(acc, m.validationStatus), ValidationStatus.Valid);
        bodyPartNode.isModified = bodyPartNode.children.some(m => m.isModified);
    }

    return sortByBodyPartDisplayOrder(bodyPartNodes);
}

// Options for filtering tree view data
export type CustomizationTreeViewFilterOptions = {
    versionTags?: string[],
    searchText?: string,
}

/**
 * Filters a customization tree by free-text search. Matches against label and tags.
 * Includes a node if it or any of its descendants match the search query.
 */
export const filterCustomizationTreeByFreeText = (
    items: CustomizationTreeViewDataItem[],
    searchText: string,
): CustomizationTreeViewDataItem[] => {
    const trimmed = (searchText || '').trim().toLowerCase();
    if (!trimmed) { return items; }

    const handleChildren = (item: CustomizationTreeViewDataItem, children: CustomizationTreeViewDataItem[]): CustomizationTreeViewDataItem | null => {
        if (item.type === CustomizationObjectType.None && (!children || children.length === 0)) {
            return null;
        }
        return { ...item, children };
    };

    const recursive = (nodes: CustomizationTreeViewDataItem[]): CustomizationTreeViewDataItem[] => {
        return nodes.map(node => {
            const fields = [node.label, ...(node.tags || [])]
                .filter(Boolean)
                .map(v => v!.toString().toLowerCase());
            const selfMatches = fields.some(f => f.includes(trimmed));

            if (selfMatches) {
                // Parent match: include node with ALL its original children unfiltered
                return { ...node, children: node.children };
            }

            const filteredChildren = node.children && node.children.length > 0 ? recursive(node.children) : [];
            if (filteredChildren && filteredChildren.length > 0) {
                return handleChildren(node, filteredChildren);
            }

            return null;
        }).filter((n): n is CustomizationTreeViewDataItem => n !== null);
    };

    return recursive(items);
};

/**
 * Retrieves all unique tags from the provided models and returns them in sorted
 * order ('latest' > version numbers in descending order > modalities > other tags 
 * in alphabetical order).
 */
export const getAllTagsInOrder = (models: Model[]): string[] => {
    const allTags = new Set<string>();
    for (const model of models) {
        for (const tag of model.tags) {
            allTags.add(tag);
        }
    }

    return getModelTagsInOrder(Array.from(allTags));
}

/**
 * Retrieves all version tags from the provided models and returns them in sorted
 * order (latest > version numbers in descending order).
 */
export const getAllVersionTagsInOrder = (models: Model[]): string[] => {
    const allVersionTags = new Set<string>();
    for (const model of models) {
        if (model.isLatest) {
            allVersionTags.add(TAG_LATEST);
        }
        if (model.version) {
            allVersionTags.add(model.version);
        }
    }

    return getModelTagsInOrder(Array.from(allVersionTags));
}
